import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { RETURN_ORDER_STATES } from "@msuite/utils/stock";
import type { Enums } from "@msuite/utils/types";

/** Props Interface */
interface ReturnOrdersStateFilterProps {
	state: Enums<"returnOrderState"> | "all";
	onChange: (state: Enums<"returnOrderState"> | "all") => void;
}

/**
 * The return list's state filter — the lifecycle in reading order, with `all` as the leading
 * segment. One state at a time: the segments carry the whole lifecycle, so which one the list is
 * showing is answered by looking at the control rather than by opening it.
 *
 * It narrows on the server rather than in the table, for the reason the order filter gives: the
 * list has never been paginated and returns every run the organisation ever raised, so filtering
 * here would still fetch all of them — and the same query param serves any other caller of
 * `GET /stock/return-orders`.
 *
 * `all` sends no state param at all, which is the endpoint's own default and the only segment that
 * shows settled and cancelled runs beside live ones.
 */
export function ReturnOrdersStateFilter({ state, onChange }: ReturnOrdersStateFilterProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	return (
		<SegmentedControl
			value={state}
			onChange={onChange}
			items={[
				{
					value: "all" as const,
					label: t("stock.filter_state_all"),
					cyId: "return-orders-state-filter-all",
				},
				...RETURN_ORDER_STATES.map((returnOrderState) => ({
					value: returnOrderState,
					// The locale spells these with the database's own hyphen — `in-progress` rather
					// than `in_progress` — so the key is the state, not a snake_cased form of it.
					label: t(`stock.return_order_state.${returnOrderState}`),
					cyId: `return-orders-state-filter-${returnOrderState}`,
				})),
			]}
		/>
	);
}
